<template>
  <div class="table-container" v-resize="calculateTableHeight">
    <div class="contacts-search" v-if="!hideSearch">
      <div class="d-flex pa-4">
        <search-toggle-field
          v-model="search"
          placeholder="Find Contacts"
          alwaysOpen
          large
          :delay="500"
        />
        <v-btn
          color="appMain"
          class="ml-4"
          :class="{ 'no-min-width': isMobile }"
          @click="toggleFilters"
        >
          <v-icon color="light" v-if="isMobile">
            {{ showFilters ? 'filter_list_off' : 'filter_list' }}
          </v-icon>
          <template v-else>
            {{ showFilters ? 'Remove Filter' : 'Filter' }}
          </template>
        </v-btn>
        <v-btn
          v-if="showSelectedButton"
          color="appMain"
          class="ml-4"
          @click="toggleSelectedList"
        >
          {{ showSelected ? 'All' : 'Selected' }}
        </v-btn>
      </div>
      <div class="pa-4 pt-0" v-if="showFilters">
        <group-filter-row
          type="attributes"
          :preparedFields="attributesFields"
          :filter="filterField"
          @refresh="refreshFilterField"
          tableFilter
        />
      </div>
    </div>

    <v-data-table
      class="styled-table contacts-table"
      item-key="con_id"
      fixed-header
      :height="tableHeight"
      :class="{ 'mr-4 ml-4': !insideModal && !hideSearch }"
      :headers="tableHeaders"
      :items="visibleContacts"
      :options.sync="options"
      :server-items-length="itemsLength"
      :footer-props="tableFooterProps"
      :show-select="showSelect"
      :mobile-breakpoint="0"
      :loading="isContactsLoading"
      v-model="selected"
      @input="$emit('input', selected)"
      @click:row="editContact"
    >
      <template v-slot:no-data>
        <div class="hint-block ma-3">
          <v-icon color="appMain" class="mb-4">info</v-icon>

          <contact-not-found
            v-if="isFiltersSet"
            :showAddButton="showAddContactButton"
            :search="filters.name"
            @created="selectCreatedContact"
          />

          <template v-else>
            <p>
              You do not have any contacts added to your account. Please add or
              import contacts to your account, then add them to this campaign.
            </p>
            <smart-tooltip
              type="contacts-an-overview"
              category="contacts"
              link
              title=" Learn about adding contacts"
            />
          </template>
        </div>
      </template>

      <template v-slot:body.prepend>
        <tr v-if="showSelectedButton && showSelected">
          <td colspan="6">
            <div class="table-message">
              <template v-if="showSelected">
                You're currently viewing only selected contacts
                <v-btn
                  class="ml-auto"
                  text
                  color="primary"
                  @click="toggleSelectedList"
                  >Show All</v-btn
                >
              </template>
            </div>
          </td>
        </tr>
      </template>

      <template v-slot:item.data-table-select="{ item, isSelected, select }">
        <v-simple-checkbox
          v-if="isUnselectable(item)"
          :value="true"
          disabled
          readonly
          color="primary"
          class="table__checkbox"
        />

        <v-simple-checkbox
          v-else
          :value="isSelected"
          color="primary"
          class="table__checkbox"
          @input="select($event)"
        />
      </template>

      <template
        v-for="header in tableHeaders"
        v-slot:[`item.${header.value}`]="{ item }"
      >
        <slot :name="[`item.${header.value}`]" :item="item">
          <contacts-table-cell
            :key="item.con_id + header.value"
            :contactId="item.con_id"
            :field="header.value"
            :fieldValue="getFieldValue(item[header.value], header.value)"
            :fieldSchema="getFieldSchema(header.value)"
            :contactName="getContactName(item)"
          />
        </slot>
      </template>

      <template v-slot:item.name="{ item }">
        <router-link
          class="text-decoration-none"
          :to="getContactPath(item)"
          :class="{ 'pointer-events-none': hideControls && !editable }"
        >
          {{ getContactName(item) }}
        </router-link>
      </template>

      <template v-slot:item.action="{ item }">
        <contact-controls :contact="item" />
      </template>
    </v-data-table>

    <contact-action-dialog />
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions, mapMutations } from 'vuex';

import { formatDistance, format } from 'date-fns';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import throttle from 'lodash/throttle';

import ContactControls from './ContactControls';
import ContactActionDialog from '@/views/contacts/contact/ContactActionDialog';
import ContactNotFound from '@/views/contacts/components/ContactNotFound';
import ContactsTableCell from '@/views/contacts/components/ContactsTableCell.vue';

import GroupFilterRow from '@/views/groups/form/GroupFilterRow';
import SearchToggleField from '@/components/basic/search-toggle-field/SearchToggleField';
import SmartTooltip from '@/components/basic/smart-tooltip/SmartTooltip';

export default {
  name: 'ContactsTable',
  components: {
    ContactControls,
    ContactActionDialog,
    ContactNotFound,
    ContactsTableCell,

    GroupFilterRow,
    SearchToggleField,
    SmartTooltip,
  },
  props: {
    outerFilters: {},
    campaignId: String,
    insideCampaign: Boolean,
    triggerId: String,
    insideModal: Boolean,
    hideControls: Boolean,
    hideSearch: Boolean,
    shortHeader: Boolean,
    showSelectedButton: Boolean,
    showAddContactButton: Boolean,
    ignoreMargin: Boolean,
    showSelect: {
      type: Boolean,
      default: true,
    },
    progressTable: Boolean,
    editable: Boolean,
    archived: String,
    customHeaders: {
      type: Array,
      default: () => [],
    },
    teamFilterOff: Boolean,
    disableSelectionForContacts: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      filters: {
        name: '',
      },
      filterField: {
        field: '',
        operator: '',
        value: '',
      },
      options: {
        sortBy: ['created_at'],
        sortDesc: [false],
        itemsPerPage: 50,
        multiSort: false,
        mustSort: false,
        page: 1,
      },
      tableHeight: 0,
      selected: [],
      defaultLimit: 50,
      showSelected: false,
      showFilters: false,
      loadingContacts: [],
      updateTimeout: null,
      progressTableFields: [],
      shortTableFields: ['name', 'mobile', 'email', 'created_at'],
    };
  },

  computed: {
    ...mapGetters(['contacts', 'contactsLoading', 'contactsInfo']),
    ...mapGetters('teams', ['currentTeam']),
    ...mapGetters('org', ['fieldSections']),
    ...mapGetters('fields', {
      attributesFields: 'attributesFields',
      defaultFields: 'defaultFields',
      fieldsLoaded: 'loaded',
      getSingleField: 'getSingleField',
    }),

    search: {
      get() {
        return this.filters.name;
      },
      set(value) {
        this.filters.name = value;
        this.searchContacts();
      },
    },

    isContactsLoading() {
      return (
        this.contactsLoading ||
        !this.fieldsLoaded ||
        this.loadingContacts.length > 0
      );
    },

    tableHeaders() {
      return this.buildTableHeaders();
    },

    tableFooterProps() {
      return {
        itemsPerPageOptions: [50],
        showCurrentPage: true,
        pageText: this.getPaginationText,
      };
    },

    filteredSelectedContacts() {
      const search = (this.filters.name || '').toLowerCase();
      const contacts = this.selected || [];
      return contacts.filter((i) => this.compareStrings(i.name, search));
    },

    itemsLength() {
      if (this.showSelected) return this.selected.length;
      return this.contactsInfo.count || this.visibleContacts.length;
    },

    visibleContacts() {
      const { page, itemsPerPage } = this.options;
      const contacts = this.showSelected
        ? this.filteredSelectedContacts
        : this.contacts;
      return contacts.slice((page - 1) * itemsPerPage, page * itemsPerPage);
    },

    isFiltersSet() {
      return (
        this.filters.name ||
        this.outerFilters ||
        this.showSelected ||
        Object.keys(this.filters).length > 2
      );
    },

    currentFilters() {
      const { page, itemsPerPage, sortBy, sortDesc } = this.options;
      const contactsParams = {
        filters: cloneDeep(this.filters),
        page: Math.ceil((page * itemsPerPage) / this.defaultLimit),
      };

      if (sortBy && sortBy.length) {
        contactsParams.sort = { [sortBy[0]]: sortDesc[0] ? 1 : -1 };
      }

      if (!contactsParams.filters.name) {
        delete contactsParams.filters.name;
      }

      return contactsParams;
    },

    // for teams
    visibleContactFields() {
      const contactFields = this.currentTeam?.contact_fields;
      if (!contactFields?.length || this.customHeaders.length) return [];

      return ['first_name', 'last_name', ...contactFields];
    },

    getPaginationText() {
      const { page, itemsPerPage } = this.options;
      const totalCount = this.itemsLength;
      if (!totalCount) return '';

      const chunkStartCount = (page - 1) * itemsPerPage + 1;
      const chunkEndCount = Math.min(page * itemsPerPage, totalCount);

      return `${chunkStartCount}-${chunkEndCount} of ${totalCount}`;
    },
  },
  watch: {
    options: {
      deep: true,
      handler(newOptionsState, oldOptionsState) {
        if (!oldOptionsState?.page) return;

        const { page, itemsPerPage } = newOptionsState;
        const contactsCount = this.contacts.length;
        const newContactsCount = page * itemsPerPage;

        if (
          newContactsCount > contactsCount &&
          this.contactsInfo?.count !== contactsCount
        ) {
          this.loadContactsChunk();
        }

        this.savePageState(page);
      },
    },
    'options.sortDesc': {
      handler(newVal, oldVal) {
        this.sortChangeHandler(newVal, oldVal);
      },
    },
    'options.sortBy': {
      handler(newVal, oldVal) {
        this.sortChangeHandler(newVal, oldVal);
      },
    },
    outerFilters: {
      deep: true,
      handler(newVal) {
        if (this.updateTimeout) {
          clearTimeout(this.updateTimeout);
        }

        this.updateTimeout = setTimeout(() => {
          let currentFilter = cloneDeep(this.filters);
          delete currentFilter['campaigns'];
          if (isEqual(newVal, currentFilter)) return;

          this.initContacts();
        }, 500);
      },
    },
    visibleContacts: {
      handler(newVal, oldVal) {
        if (this.progressTable && !isEqual(newVal, oldVal)) {
          this.loadContactsProgress();
        }
      },
    },
  },
  methods: {
    ...mapMutations(['setContacts', 'setContactsInfo']),
    ...mapActions(['loadContacts', 'loadContactMetric']),
    ...mapActions('fields', { loadFields: 'load' }),

    isUnselectable({ con_id }) {
      if (!this.disableSelectionForContacts.length || !con_id) return false;
      return this.disableSelectionForContacts.includes(con_id);
    },

    getFieldSchema(field) {
      return this.getSingleField(field);
    },

    getFieldType(field) {
      const fieldSchema = this.getFieldSchema(field);
      return fieldSchema?.type;
    },

    getFieldValue(value, field) {
      if (this.getFieldType(field) === 'time') {
        return this.formatDate(value, field);
      }

      if (['points', 'maxPoints'].includes(field)) {
        return (value || 0).toLocaleString('en-US');
      }

      if (['progress', 'grade'].includes(field)) {
        const isNumeric = typeof value === 'number';
        return isNumeric ? `${Math.round(value * 100)}%` : '';
      }

      return value;
    },

    isFieldVisible(field) {
      const { active, label, name } = field;
      if (
        !active ||
        label === 'name' ||
        ['campaigns', 'created_at'].includes(name)
      ) {
        return false;
      }
      if (!this.visibleContactFields.length) return true;
      return this.visibleContactFields.includes(name);
    },

    customFieldSort(fields) {
      const sectionOrder = (this.fieldSections || []).map((s) => s.slug);

      return fields.sort((a, b) => {
        const aIndex = sectionOrder.indexOf(a.section || '');
        const bIndex = sectionOrder.indexOf(b.section || '');

        return aIndex !== bIndex
          ? (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex)
          : (a.order || 0) - (b.order || 0);
      });
    },

    getContactName(contact) {
      const { first_name, last_name } = contact;
      if (!first_name && !last_name) return 'unknown';
      return `${first_name} ${last_name}`;
    },

    buildTableHeaders() {
      if (!this.fieldsLoaded) return [];

      let headers = [
        {
          text: 'Name',
          value: 'name',
          cellClass: 'fixed',
          class: 'fixed',
          width: 180,
          sortable: true,
        },
        {
          text: 'Created At',
          value: 'created_at',
          sortable: true,
          width: 160,
        },
      ];

      if (!this.hideControls) {
        headers.unshift({
          text: '',
          value: 'action',
          cellClass: 'fixed pl-2 pr-0',
          class: 'fixed',
          width: 50,
          sortable: false,
        });
      }

      if (this.defaultFields && !this.isMobile) {
        // Sort fields by section first, then by order within section
        const sortedFields = this.customFieldSort([...this.defaultFields]);

        headers = sortedFields.reduce((acc, field) => {
          if (this.isFieldVisible(field)) {
            acc.push({
              text: field.label,
              value: field.name,
              sortable: true,
              iskey: field.iskey,
              name: field.name,
              width: field.type === 'time' ? 220 : undefined,
            });
          }

          return acc;
        }, headers);
      }

      if (this.shortHeader && !this.isMobile) {
        headers = headers.filter((i) => {
          return this.shortTableFields.includes(i.value) || i.iskey;
        });
      }

      if (this.customHeaders.length) {
        headers = this.customHeaders;
      }

      return headers;
    },

    selectAllContacts() {
      this.selected = [...new Set([...this.selected, ...this.visibleContacts])];
    },

    selectCreatedContact(contact) {
      this.selected.push(contact);
    },

    savePageState(page) {
      const filters = cloneDeep(this.contactsInfo.filters);
      if (!filters || filters.page === page) return;

      filters.page = page;
      this.setContactsInfo({ count: this.contactsInfo.count, filters });
    },

    sortChangeHandler(newVal, oldVal) {
      if (!isEqual(newVal, oldVal) && oldVal !== undefined) {
        this.searchContacts();
      }
    },

    loadContactsProgress() {
      this.visibleContacts.forEach((contact) => {
        let contactId = contact.con_id;
        if (
          contact.progress !== undefined ||
          this.loadingContacts.includes(contactId)
        )
          return;

        this.loadingContacts.push(contactId);

        this.loadContactMetric({
          resource: 'campaign_progress',
          filters: {
            con_id: contact.con_id,
            campaign_id: this.campaignId,
          },
        }).then(() => {
          this.loadingContacts = this.loadingContacts.filter(
            (i) => i !== contactId
          );
        });
      });
    },

    searchContacts() {
      this.clearContacts();
      this.loadContactsChunkThrottle();
    },

    toggleSelectedList() {
      this.options.page = 1;
      this.showSelected = !this.showSelected;
      if (this.showSelected) {
        this.filters.name = '';
      }
    },

    formatDate(timestamp, field) {
      if (!timestamp) {
        return '';
      }

      if (typeof timestamp === 'number') {
        timestamp = timestamp * 1000;
      }

      const date = new Date(timestamp);

      if (field === 'Last Seen') {
        return formatDistance(date, new Date(), {
          addSuffix: true,
        });
      }

      const formatted = format(date, 'MMM dd yyyy, hh:mm a');
      const tz = date
        .toLocaleTimeString('en-us', { timeZoneName: 'short' })
        .split(' ')[2];

      return `${formatted} ${tz}`;
    },

    clearContacts() {
      if (isEqual(this.currentFilters, this.contactsInfo.filters)) return;

      this.options.page = 1;
      this.setContacts([]);
    },

    setTableOptions() {
      const storedFilters = this.contactsInfo.filters;

      if (storedFilters) {
        // set options such as page
        for (let key in this.options) {
          if (storedFilters[key]) {
            this.options[key] = storedFilters[key];
          }
        }

        // set sort from store
        // table sort = ts / server sort = ss
        // ts = sortDesc:[false] | ss = sort: -1
        // ts = sortDesc:[true]  | ss = sort: 1
        // ts = sortDesc[]       | ss =
        if (storedFilters.sort) {
          this.options.sortBy = Object.keys(storedFilters.sort);
          this.options.sortDesc = [
            storedFilters.sort[this.options.sortBy] === 1,
          ];
        } else {
          this.options.sortBy = [];
          this.options.sortDesc = [];
        }
      }

      if (this.outerFilters) {
        this.filters = cloneDeep(this.outerFilters);
      }

      if (this.campaignId) {
        let filter = this.insideCampaign ? '$eq' : '$ne';
        this.filters['campaigns'] = { [filter]: this.campaignId };
      }
    },

    async initContacts() {
      // assign stored filters
      this.setTableOptions();
      await this.$nextTick();

      // clear table data if filters changed
      this.clearContacts();

      this.loadContactsChunkThrottle();
    },

    loadContactsChunkThrottle: throttle(function () {
      this.loadContactsChunk();
    }, 1000),

    loadContactsChunk() {
      const { filters, archived } = this.contactsInfo;
      const isEqualFilters = isEqual(this.currentFilters, filters);
      const isEqualArchived = (this.archived || false) === (archived || false);

      if (isEqualFilters && isEqualArchived) {
        return;
      }

      this.loadContacts({
        filters: this.currentFilters,
        fields: this.visibleContactFields,
        archived: this.archived,
        format: 'v2',
        teamFilterOff: this.teamFilterOff,
      });
    },

    getContactPath(contact) {
      return {
        name: 'contact-edit',
        params: {
          id: contact.con_id,
          contactData: contact,
        },
      };
    },

    editContact(contact) {
      if (this.progressTable) {
        this.$emit('show', contact);
      }

      if (this.hideControls && !this.editable) {
        return false;
      }

      this.$router.push(this.getContactPath(contact));
    },

    compareStrings(fullString, search) {
      return fullString.toLowerCase().includes(search);
    },

    toggleFilters() {
      this.showFilters = !this.showFilters;
      if (!this.showFilters) {
        this.removeFilterField();
        this.searchContacts();
      }
      setTimeout(() => {
        this.calculateTableHeight();
      }, 100);
    },

    removeFilterField() {
      for (let key in this.filters) {
        if (key !== 'campaigns' && key !== 'name') {
          Vue.delete(this.filters, key);
        }
      }
    },

    async refreshFilterField() {
      this.removeFilterField();

      const filter = this.filterField;
      if (filter.field && filter.operator) {
        Vue.set(this.filters, filter.field.value, {
          [filter.operator.operator || filter.operator.value]: filter.value,
        });
      }

      this.searchContacts();
    },

    clearContactsTable() {
      this.removeFilterField();
      this.selected = [];
      this.showSelected = false;
    },

    async calculateTableHeight() {
      if (this.insideModal) await this.$nextTick();

      const tableContainer = document.querySelector('.table-container');
      const tableFooterHeight = 39;
      if (!tableContainer) return;

      let tableHeight = tableContainer.clientHeight - tableFooterHeight;
      if (!this.hideSearch) {
        const searchBar = document.querySelector('.contacts-search');
        tableHeight -= searchBar.clientHeight;

        if (!this.progressTable && !this.ignoreMargin) {
          tableHeight -= 45;
        }
      }

      this.tableHeight = tableHeight;
    },
  },
  mounted() {
    this.loadFields({ checkLoaded: true });
    this.initContacts();
    this.calculateTableHeight();
  },
};
</script>

<style lang="less">
.contacts-table {
  position: relative;
  table {
    background-color: #fff;
    th {
      text-transform: capitalize;
    }
  }
}

.groups-modal {
  .contacts-table {
    height: 100%;
    .v-data-table__wrapper {
      overflow: auto !important;
      height: 100%;
    }
  }
}

.contact-name {
  color: var(--v-primary-base);

  @media (max-width: 980px) {
    font-size: 16px;
  }
}

.table-container {
  height: 100%;
}

.table__checkbox,
.v-data-table__checkbox {
  width: 18px;
  .v-input--selection-controls__input {
    margin-right: 0 !important;
  }
}
</style>
